import type { SettingsLeaf } from '@/components/nav-v2/settings/settings-ia';
import { findClusterForPath } from '@/components/nav-v2/settings/settings-ia';
import { isPayEventFactQueuePath } from '@/lib/payroll/pay-event-fact-integrity';
import { isOwnNotificationPreferencePath } from './settings-access-roles';
import { resolveBaseSettingsAllowedRoles } from './settings-base-access';
import type { SettingsDelegationPreference } from './settings-delegation';

/**
 * Release 1 owner carve-out (payroll, leave, Xero): main 764808c5cd's Settings
 * delegation semantics, kept verbatim so these destinations behave exactly as
 * they do on main in every persisted state until the owner's release 2 review.
 * doc48-release1-owner-parity proves equality with main's own functions.
 */
const ADMIN_ROLES = new Set(['SYSTEM_ADMIN', 'TENANT_ADMIN']);

function isOperatorOnlyCohort(roles: readonly string[]): boolean {
  return roles.length === 1 && roles[0] === 'SYSTEM_ADMIN';
}

function applyMainDelegationToRoleList(
  baseRoles: readonly string[],
  clusterId: string | null | undefined,
  delegation: SettingsDelegationPreference | null | undefined,
): readonly string[] {
  if (!clusterId || !delegation) return baseRoles;
  const denies = new Set(delegation.deniedRolesByCluster[clusterId] ?? []);
  const withoutDenials = baseRoles.filter((role) => ADMIN_ROLES.has(role) || !denies.has(role));
  if (isOperatorOnlyCohort(baseRoles)) return withoutDenials;

  const grants = delegation.grantedRolesByCluster[clusterId] ?? [];
  return [...new Set([...withoutDenials, ...grants])];
}

/** Main's resolveSettingsAllowedRoles. */
export function mainOwnerSettingsAllowedRoles(
  pathname: string,
  delegation?: SettingsDelegationPreference | null,
): readonly string[] {
  pathname = pathname.split(/[?#]/, 1)[0] ?? pathname;
  const base = resolveBaseSettingsAllowedRoles(pathname);
  if (pathname.startsWith('/settings/compliance')) return base;
  if (!delegation) return base;
  if (isPayEventFactQueuePath(pathname)) return base;
  if (isOwnNotificationPreferencePath(pathname)) return base;
  const match = findClusterForPath(pathname);
  const clusterId = match?.item.delegationClusterId ?? match?.cluster.id;
  return applyMainDelegationToRoleList(base, clusterId, delegation);
}

/** Main's isSettingsLeafAllowedForRole. */
export function mainOwnerSettingsLeafAllowedForRole(
  leaf: Pick<SettingsLeaf, 'href' | 'roles'>,
  viewerRole: string | null | undefined,
  delegation: SettingsDelegationPreference | null | undefined,
): boolean {
  if (!viewerRole) return false;
  if (leaf.href === '/settings/compliance') return (leaf.roles ?? []).includes(viewerRole);
  const match = findClusterForPath(leaf.href);
  const clusterId = match?.item.delegationClusterId ?? match?.cluster.id;
  const baseRoles = leaf.roles ?? [];
  if (viewerRole === 'SYSTEM_ADMIN') return true;
  if (viewerRole === 'TENANT_ADMIN') return !isOperatorOnlyCohort(baseRoles);

  if (delegation && clusterId) {
    const denied = delegation.deniedRolesByCluster[clusterId] ?? [];
    if (denied.includes(viewerRole)) return false;
  }
  if (baseRoles.includes(viewerRole)) return true;
  if (isOperatorOnlyCohort(baseRoles)) return false;

  const grants = delegation?.grantedRolesByCluster[clusterId ?? ''] ?? [];
  return grants.includes(viewerRole);
}
